/**
 * Event-payload builders for the commit funnel (T26 — conversion telemetry
 * baseline). Same event names + property keys are used both before and after
 * the Confirm-step removal flag flips; only the `source_step` attribute differs.
 *
 * Dashboard query (Looker / GA4) — pre/post comparison:
 *   funnel: pageview(source=confirm|guarantee|personalize)
 *         → booking_commit_attempt(filtered by source_step)
 *         → reservation_complete (success)  OR  booking_commit_failure (error)
 *
 * Abandonment is derived: arrivals - attempts per session.
 */

import { ANALYTICS_EVENTS } from './analyticsEvents';
import type { AnalyticsEvent } from './types';

/**
 * Backend-error codes the commit funnel may surface verbatim. These are the
 * same code-shaped strings used in `useReservationCommit`'s
 * `backendErrorMessages` mapping table. Anything outside this set is treated
 * as unverified text and either truncated (for debug-ability) or dropped.
 *
 * Shared between `categorizeCommitError` and `safeErrorCode` so the set lives
 * in one place — adding a new code requires touching one constant.
 */
const KNOWN_BACKEND_ERROR_CODES = new Set<string>([
  'RESTAURANT_INACTIVE',
  'BLOCKED_USER_SUBTITLE',
  'MONTHLY_LIMIT_REACHED',
]);

/**
 * Max length we'll forward to GA when the message isn't a known code. Long
 * enough to keep debug context, short enough to bound PII risk if a future
 * thrower builds an Error from user input or backend payload data.
 */
const ERROR_CODE_MAX_LENGTH = 64;

/**
 * Which booking step hosted the commit click. Determines which leg of the
 * funnel the event belongs to.
 *
 * - `confirm` — the legacy Confirm step at /confirmation (pre-flag-flip)
 * - `guarantee` — the Guarantee step (post-flip, when fee > 0)
 * - `personalize` — the Personalize step (post-flip, when no guarantee fee)
 *
 * Other values are unexpected (mis-host or future step) and tagged 'unknown'
 * so the dashboard surfaces them rather than dropping the event.
 */
export type CommitSourceStep = 'confirm' | 'guarantee' | 'personalize' | 'unknown';

/**
 * Map an URL pathname to the corresponding source step. Centralized so the
 * hook + any test scaffolding agree on the mapping.
 *
 * Returns 'unknown' for unrecognized paths. Such events still land in GA
 * tagged as 'unknown' so the dashboard surfaces them — but we also emit a
 * Sentry breadcrumb so the drift is noticed at debug time, not weeks later
 * when someone notices a funnel row that doesn't fit.
 */
export function deriveSourceStepFromPath(pathname: string): CommitSourceStep {
  if (pathname === '/confirmation') return 'confirm';
  if (pathname === '/confirm-guarantee') return 'guarantee';
  if (pathname === '/personalize') return 'personalize';
  // Lazy import to avoid Sentry coupling in pure helpers; safe inside the
  // fallback branch.
  if (typeof window !== 'undefined') {
    import('@sentry/react').then((Sentry) => {
      Sentry.addBreadcrumb({
        category: 'commit-funnel',
        message: 'Unknown source_step pathname',
        level: 'warning',
        data: { pathname },
      });
    }).catch(() => { /* non-blocking */ });
  }
  return 'unknown';
}

/**
 * Categorize a commit error into a small set of buckets the dashboard can
 * pivot on. Keep the bucket list short — the goal is funnel monitoring, not
 * granular error analysis (use Sentry for that).
 */
export type CommitErrorCategory =
  | '3ds_rejected'
  | 'card_declined'
  | 'backend_blocked'    // RESTAURANT_INACTIVE, BLOCKED_USER, MONTHLY_LIMIT
  | 'network'            // fetch/timeout
  | 'unknown';

/**
 * Map a raw cause + ThreeDsError flag into a coarse category. `causeName` is
 * the `Error.name` (e.g. 'ThreeDsError'); `causeMessage` is `Error.message`.
 */
export function categorizeCommitError(opts: {
  causeName: string | undefined;
  causeMessage: string;
}): CommitErrorCategory {
  const { causeName, causeMessage } = opts;
  if (causeName === 'ThreeDsError') return '3ds_rejected';
  // Backend errors arrive as a raw Error with the code as the message
  // (see useReservationCommit's mapping table).
  if (KNOWN_BACKEND_ERROR_CODES.has(causeMessage)) return 'backend_blocked';
  // MP card decline strings — multi-language, but easy to recognize by content
  if (/no procesó el pago|n[aã]o ter processado|have not processed|Customer not found/i.test(causeMessage)) {
    return 'card_declined';
  }
  if (/network|fetch|timeout|ECONNREFUSED|ECONNRESET/i.test(causeMessage)) return 'network';
  return 'unknown';
}

/**
 * Common attribution attached to every commit funnel event.
 */
export interface CommitFunnelAttribution {
  source_step: CommitSourceStep;
  guarantee_type: string;           // booking.guaranteeType or 'early_access'
  payment_processor: 'stripe' | 'mercadopago' | 'none';
  is_waiting_list: boolean;
  time_on_step_ms: number;          // hook mount → commit click
}

/**
 * Build a BOOKING_COMMIT_ATTEMPT event. Fire once per commit click, before
 * the API call.
 */
export function makeCommitAttemptEvent(attribution: CommitFunnelAttribution): AnalyticsEvent {
  return {
    name: ANALYTICS_EVENTS.BOOKING_COMMIT_ATTEMPT,
    properties: { ...attribution },
  };
}

/**
 * Constrain the `error_code` we forward to GA. Today's commit-error throwers
 * produce code-shaped strings (`'BLOCKED_USER_SUBTITLE'`,
 * `'RESTAURANT_INACTIVE'`, `'MONTHLY_LIMIT_REACHED'`, MP localized templates
 * without PII), but the path is a bare `Error.message` passthrough — a future
 * thrower that builds an Error from user input or a backend payload would
 * leak that string verbatim to Google Analytics.
 *
 * Strategy (hybrid):
 *   - `ThreeDsError` → its discrete `.code` field (already an enum)
 *   - Message in `KNOWN_BACKEND_ERROR_CODES` → passthrough (debug-friendly)
 *   - Any other `Error.message` → truncate to {@link ERROR_CODE_MAX_LENGTH}
 *   - Non-Error cause → undefined (drop the field entirely)
 *
 * The hook still routes the raw error to Sentry via `capturePaymentError`
 * — full context is preserved upstream of GA.
 */
export function safeErrorCode(cause: unknown): string | undefined {
  // Use duck-typing for ThreeDsError so we don't import a feature module from
  // a core util (and avoid the circular-dep risk). The `name === 'ThreeDsError'`
  // + `typeof code === 'string'` shape is sufficient and matches the class
  // exported from `@features/booking/hooks/handle3dsChallenge`.
  if (
    cause instanceof Error &&
    cause.name === 'ThreeDsError' &&
    typeof (cause as Error & { code?: unknown }).code === 'string'
  ) {
    return (cause as Error & { code: string }).code;
  }
  if (!(cause instanceof Error)) return undefined;
  if (KNOWN_BACKEND_ERROR_CODES.has(cause.message)) return cause.message;
  return cause.message.slice(0, ERROR_CODE_MAX_LENGTH);
}

/**
 * Build a BOOKING_COMMIT_FAILURE event. Fire from the hook's catch block,
 * after the CommitError unwrapping logic.
 */
export function makeCommitFailureEvent(
  attribution: CommitFunnelAttribution,
  errorOpts: {
    error_category: CommitErrorCategory;
    error_code?: string;
  },
): AnalyticsEvent {
  return {
    name: ANALYTICS_EVENTS.BOOKING_COMMIT_FAILURE,
    properties: { ...attribution, ...errorOpts },
  };
}
